import {
    SOCKET_IO_PATH,
    type CrucibleConnectionStatus,
    type CrucibleMember,
    type CrucibleSessionConnection,
    type PartyClientToServerEvents,
    type PartyConnectRefusal,
    type PartyServerToClientEvents,
    type StartingCrystalPick,
} from '@sorrem/shared';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { setStoredCrucibleJoinSession } from '@sorrem-client/ui/features/PlayPage/stored-join-session';
import { getStoredSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { getStoredSessionToken, getStoredAccountId } from '@sorrem-client/ui/shared/auth-session';
import { useStoreSelector } from '@sorrem-client/ui/hooks/use-store-selector';
import {
    applyPartyEvent,
    INITIAL_PARTY_REALTIME_STATE,
    type PartyIssue,
    type PartyRealtimeEvent,
    type PartyRealtimeState,
} from './party-realtime-state';
import { awaitAck, NO_ANSWER_ERROR, resolveAckError, type PartyAckOnlyEvent } from './party-requests';

let state = INITIAL_PARTY_REALTIME_STATE;
let socket: Socket<PartyServerToClientEvents, PartyClientToServerEvents> | null = null;
let connectedToken: string | null = null;
let connectedCrucibleId: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
    for (const listener of listeners) {
        listener();
    }
}

/** Moves the store on by one event (see `applyPartyEvent`) and tells every reader. */
function dispatch(event: PartyRealtimeEvent): void {
    state = applyPartyEvent(state, event);
    notify();
}

function resetState(): void {
    state = INITIAL_PARTY_REALTIME_STATE;
    notify();
}

function teardownSocket(): void {
    if (socket) {
        socket.off();
        socket.disconnect();
        socket = null;
        connectedToken = null;
        connectedCrucibleId = null;
    }
}

function connectSocket(crucibleId: string): Socket<PartyServerToClientEvents, PartyClientToServerEvents> | null {
    if (crucibleId.length === 0) {
        teardownSocket();
        resetState();
        return null;
    }

    const token = getStoredSessionToken();
    if (token === null) {
        dispatch({ type: 'issue', issue: { kind: 'session-expired', message: 'Your session expired. Please log in again.' } });
        teardownSocket();
        return null;
    }

    const characterId = getStoredSelectedCharacterId();
    if (characterId === null) {
        dispatch({ type: 'issue', issue: { kind: 'fatal', message: 'No character selected for this session.' } });
        teardownSocket();
        return null;
    }

    if (socket && connectedToken === token && connectedCrucibleId === crucibleId) {
        if (socket.disconnected) {
            // socket.io only auto-reconnects after a network drop, not after the
            // server closes the connection - resume it explicitly so a stale
            // cached socket doesn't silently swallow the next party action.
            socket.connect();
        }
        return socket;
    }

    teardownSocket();

    dispatch({ type: 'connecting', crucibleId });

    const nextSocket = io('/party', {
        path: SOCKET_IO_PATH,
        query: { crucibleId },
        auth: { token, characterId },
    });

    socket = nextSocket;
    connectedToken = token;
    connectedCrucibleId = crucibleId;

    nextSocket.on('connect', () => {
        dispatch({ type: 'connect' });
        nextSocket.emit('party:sync');
    });
    nextSocket.on('disconnect', () => dispatch({ type: 'disconnect' }));
    nextSocket.on('connect_error', (error) => {
        // The server says why in `data.code` (PartyConnectRefusal).
        const code = (error as Error & { data?: { code?: PartyConnectRefusal } }).data?.code;
        dispatch({ type: 'connect_error', code, message: error.message || 'Unable to connect to the party.' });
    });
    nextSocket.on('party:state', (partyState) => dispatch({ type: 'party:state', crucibleId, partyState, nowMs: Date.now() }));
    nextSocket.on('party:started', () => dispatch({ type: 'party:started', nowMs: Date.now() }));
    nextSocket.on('party:start-refused', (refusal) => dispatch({ type: 'party:start-refused', message: refusal.message }));
    nextSocket.on('party:ready', (payload) => {
        if (payload.accountId !== getStoredAccountId()) {
            return;
        }
        const sessionConnection = payload.joinResponse.assignment.connection;
        setStoredCrucibleJoinSession({ crucibleId, sessionConnection });
        dispatch({ type: 'party:ready', sessionConnection });
    });
    nextSocket.on('party:member-removed', (member) => {
        if (member.accountId !== getStoredAccountId() || member.characterId !== characterId) {
            return;
        }
        dispatch({ type: 'party:member-removed' });
        // Drop the socket so the follow-up party snapshot never reaches this client.
        teardownSocket();
    });
    nextSocket.on('party:error', (message) => dispatch({ type: 'party:error', message }));
    nextSocket.on('party:chat-message', (message) => dispatch({ type: 'party:chat-message', message }));

    return nextSocket;
}

/** Sends a party chat message over the shared party socket. No-ops if not currently connected. */
export function sendPartyChatMessage(crucibleId: string, body: string): void {
    if (!socket || !socket.connected || connectedCrucibleId !== crucibleId) {
        return;
    }

    socket.emit('party:chat:send', { body });
}

/** Creator only: removes one other member (by character) from the unstarted party. */
export async function removePartyMember(crucibleId: string, characterId: string): Promise<boolean> {
    const currentSocket = connectSocket(crucibleId);
    if (!currentSocket) {
        return false;
    }

    const response = await awaitAck((ack) => {
        currentSocket.emit('party:remove-member', { characterId }, ack);
    });
    const error = resolveAckError(response);
    if (error !== null) {
        // Non-fatal: keep the gathering connected and surface the reason.
        dispatch({ type: 'issue', issue: { kind: 'notice', message: error } });
    }
    return error === null;
}

/**
 * Marks this member ready (or not) to enter. Resolves with null when the
 * server accepted it, or the reason it didn't.
 */
export async function setPartyReady(crucibleId: string, isReady: boolean): Promise<string | null> {
    const currentSocket = connectSocket(crucibleId);
    if (!currentSocket) {
        return 'Not connected to the party.';
    }

    return resolveAckError(await awaitAck((ack) => {
        currentSocket.emit('party:set-ready', { isReady }, ack);
    }));
}

/**
 * Replaces the crystals this member has coalesced on the gathering page.
 * Resolves with null when the server accepted them, or the reason it didn't.
 */
export async function stagePartyStartingCrystals(crucibleId: string, picks: StartingCrystalPick[]): Promise<string | null> {
    const currentSocket = connectSocket(crucibleId);
    if (!currentSocket) {
        return 'Not connected to the party.';
    }

    return resolveAckError(await awaitAck((ack) => {
        currentSocket.emit('party:stage-crystals', { picks }, ack);
    }));
}

/** Sends one of the answer-only requests; resolves with whether the server accepted it. */
async function emitAck(eventName: PartyAckOnlyEvent, crucibleId: string): Promise<boolean> {
    const currentSocket = connectSocket(crucibleId);
    if (!currentSocket) {
        return false;
    }

    const response = await awaitAck((ack) => {
        currentSocket.emit(eventName, ack);
    });
    if (response === null) {
        dispatch({ type: 'issue', issue: { kind: 'notice', message: NO_ANSWER_ERROR } });
        return false;
    }
    if (response.ok) {
        return true;
    }
    // No room to start right now (#214): a notice with a retry, not a dead end.
    if (response.code === 'start-refused') {
        dispatch({ type: 'party:start-refused', message: response.error });
        return false;
    }
    // Refused (not the creator, already started...): said, and the party stays connected.
    dispatch({ type: 'issue', issue: { kind: 'notice', message: response.error } });
    return false;
}

export function subscribePartyRealtime(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            teardownSocket();
            state = INITIAL_PARTY_REALTIME_STATE;
        }
    };
}

function getPartyRealtimeState(): PartyRealtimeState {
    return state;
}

/**
 * Reads a slice of the party store, re-rendering only when it changes by
 * `isEqual` (tearing-safe: built on `useSyncExternalStore`). Connecting to
 * `crucibleId` is a side effect of every caller.
 */
export function usePartyRealtimeSelector<T>(
    crucibleId: string | null,
    selector: (storeState: PartyRealtimeState) => T,
    isEqual: (left: T, right: T) => boolean = Object.is,
): T {
    useEffect(() => {
        connectSocket(crucibleId ?? '');
    }, [crucibleId]);

    return useStoreSelector(subscribePartyRealtime, getPartyRealtimeState, selector, isEqual);
}

export function startPartyCrucible(crucibleId: string): Promise<boolean> {
    return emitAck('party:start', crucibleId);
}

export function leavePartyCrucible(crucibleId: string): Promise<boolean> {
    return emitAck('party:leave', crucibleId);
}

export function removePartyCrucible(crucibleId: string): Promise<boolean> {
    return emitAck('party:remove', crucibleId);
}

export type { CrucibleConnectionStatus, CrucibleMember, CrucibleSessionConnection, PartyIssue, PartyRealtimeState };
